/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { oc } from 'ts-optchain';
import _ from 'lodash';

import * as globalEventsHelper from '@tock/shared/utils/globalEventsHelper';
import { GRID_SIZE, getIPadDimensions } from 'constants/AerialConstants';
import { IconRotateClockwise, IconRotateCounterClockwise } from '@tock/icons';
import Popover from 'components/Popover';
import PureBaseComponent from '@tock/shared/components/PureBaseComponent';
import TablePopoverShapeSelector from 'components/settings/tables/edit-popover/TablePopoverShapeSelector';
import TablePopoverVisualSizeSelector from 'components/settings/tables/edit-popover/TablePopoverVisualSizeSelector';

const ESC_KEY = 27;
const POPOVER_HEIGHT = Math.ceil(351 / GRID_SIZE);
const POPOVER_WIDTH = Math.ceil(264 / GRID_SIZE);

interface Props {
  id: number;
  location: TableLocation;
  name: string;
  onClose(tableId: number): void;
  onResetTableLocation(tableId: number): void;
  onUpdateTableLocation(tableId: number, tableLocation: TableLocation): void;
}

export default class AerialTableEditPopover extends PureBaseComponent<Props> {
  onEscape(event: KeyboardEvent) {
    if (event.keyCode === ESC_KEY) {
      this.props.onClose(this.props.id);
    }
  }

  handleClickOutside() {
    this.props.onClose(this.props.id);
  }

  componentDidMount() {
    document.addEventListener('keydown', this.onEscape);
    globalEventsHelper.addGlobalClickEvent(this.handleClickOutside, 'popover-component');
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onEscape);
    globalEventsHelper.removeGlobalClickEvent(this.handleClickOutside, 'popover-component');
  }

  getEditPopoverPosition() {
    const location = this.props.location;

    let align = 'center';
    let justify = 'end';
    if (location.yPosition + POPOVER_HEIGHT < getIPadDimensions(1024, 768).maxY) {
      align = 'start';
    } else if (location.yPosition + location.visualSize > POPOVER_HEIGHT) {
      align = 'end';
    }

    if (location.xPosition > POPOVER_WIDTH) {
      justify = 'start';
    }

    return {
      align,
      position: 'horizontal',
      justify,
    };
  }

  handleChangeShape(event: React.MouseEvent<HTMLElement>) {
    const newShape = _.toNumber(event.currentTarget.getAttribute('data-name'));
    if (newShape === this.props.location.shape) {
      return;
    }
    this.handleUpdateLocation({
      shape: newShape,
    });
  }

  handleChangeVisualSize(event: React.MouseEvent<HTMLElement>) {
    const newVisualSize = _.toNumber(event.currentTarget.getAttribute('data-name'));
    if (newVisualSize === this.props.location.visualSize) {
      return;
    }
    this.handleUpdateLocation({
      visualSize: newVisualSize,
    });
  }

  handleReset(event: React.MouseEvent<HTMLElement>) {
    event.preventDefault();
    this.props.onResetTableLocation(this.props.id);
  }

  handleRotateLeft() {
    const currentRotation = oc(this.props.location).rotation(0);
    const newRotation = currentRotation - 90;
    this.handleUpdateLocation({
      rotation: newRotation >= 0 ? newRotation : 270,
    });
  }

  handleRotateRight() {
    const currentRotation = oc(this.props.location).rotation(0);
    const newRotation = currentRotation + 90;
    this.handleUpdateLocation({
      rotation: newRotation <= 270 ? newRotation : 0,
    });
  }

  handleUpdateLocation(newValueObj: any) {
    const newLocation = {
      ...this.props.location,
      ...newValueObj,
    };
    this.props.onUpdateTableLocation(this.props.id, newLocation);
  }

  render() {
    const { location } = this.props;
    const popoverPosition = this.getEditPopoverPosition() as any;

    return (
      <Popover {...popoverPosition} isOpen={true} onClose={_.noop}>
        <div className="AerialTableEditPopover">
          <div className="AerialTableEditPopover-row">
            <div className="AerialTableEditPopover-rowName">Table {this.props.name}</div>
            <div className="AerialTableEditPopover-resetLinkContainer">
              <a className="AerialTableEditPopover-resetLink" href="" onClick={this.handleReset}>
                Reset
              </a>
            </div>
          </div>
          <div className="AerialTableEditPopover-row">
            <div className="AerialTableEditPopover-rowName">Size</div>
            <div className="AerialTableEditPopover-rowSelector">
              <TablePopoverVisualSizeSelector
                selectedSize={_.toString(location.visualSize)}
                onSelect={this.handleChangeVisualSize}
              />
            </div>
          </div>
          <div className="AerialTableEditPopover-shapesRow">
            <TablePopoverShapeSelector
              onSelect={this.handleChangeShape}
              selectedShape={_.toString(location.shape)}
            />
          </div>
          <div className="AerialTableEditPopover-row">
            <div className="AerialTableEditPopover-rowName">Orientation</div>
            <div className="AerialTableEditPopover-rowSelector AerialTableEditPopover-rotator">
              <div className="AerialTableEditPopover-rotatorButton" onClick={this.handleRotateLeft}>
                <IconRotateCounterClockwise />
              </div>
              <div
                className="AerialTableEditPopover-rotatorButton"
                onClick={this.handleRotateRight}
              >
                <IconRotateClockwise />
              </div>
            </div>
          </div>
        </div>
      </Popover>
    );
  }
}
